import { useTranslation } from 'react-i18next'

import { useCopyLink } from '#/shared/lib/clipboard'
import { Button } from '#/shared/ui/button'
import { ContractEditor } from '#/shared/ui/contract-editor'
import { SendIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'

import { openContractPreview, toContractRow } from './contract-mappers'
import type { PanelState } from './panel-state'

function ContractEmpty({
  onSend,
  isSending,
}: {
  onSend?: () => void
  isSending?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-35 w-full flex-col items-center justify-center gap-3.75 px-3.75">
      <p className="max-w-81 text-center text-sm text-gray-500">
        {t('orderDetails.comment.contract.emptyDescription')}
      </p>
      <Button
        size="xs"
        pressEffect
        disabled={isSending}
        className="gap-2.5 px-3.75"
        onClick={onSend}
      >
        {isSending ? (
          <span
            aria-hidden
            className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          <SendIcon className="size-3.5" />
        )}
        {isSending
          ? t('table.saving')
          : t('orderDetails.comment.contract.send')}
      </Button>
    </div>
  )
}

function ContractRowSkeleton() {
  return (
    <div className="flex w-full items-center justify-between gap-2.5 px-4 py-3.5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <Skeleton className="h-5 w-24 rounded-full" />
        <Skeleton className="h-8 w-16 rounded-md" />
      </div>
    </div>
  )
}

function ContractLoading() {
  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-col">
        <ContractRowSkeleton />
        <div className="px-4">
          <span className="block h-px w-full bg-gray-200" />
        </div>
        <ContractRowSkeleton />
      </div>
      <div className="flex h-14 w-full items-center justify-end border-t border-gray-200 px-2.5">
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
    </div>
  )
}

export function ContractTab({
  contract,
  compact,
  onClose,
}: {
  contract: PanelState['contract']
  compact?: boolean
  onClose?: () => void
}) {
  const copyLink = useCopyLink()
  if (contract.isLoading) return <ContractLoading />
  if (contract.items.length === 0) {
    return (
      <ContractEmpty onSend={contract.onSend} isSending={contract.isSending} />
    )
  }
  return (
    <ContractEditor
      compact={compact}
      onCancel={onClose}
      contracts={contract.items.map(toContractRow)}
      onSendNew={contract.onSend}
      onPreview={openContractPreview}
      onSaveNote={(row, note) => contract.onSaveNote(row.id, note)}
      onToggleCca={(row, next) => contract.onToggleCca(row.id, next)}
      onCopySignLink={(row) => {
        if (row.signLink) void copyLink(row.signLink)
      }}
      isSendingNew={contract.isSending}
    />
  )
}
